import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Bell, CheckCheck } from 'lucide-react';
import { api } from '@/api/cliente';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContenido, PopoverDisparador } from '@/components/ui/overlay';
import { cn, fechaHora } from '@/lib/utils';

interface Notificacion {
  id: string;
  titulo: string;
  cuerpo: string;
  enlace: string | null;
  leidaEn: string | null;
  createdAt: string;
}

/** Campana con la bandeja de notificaciones del usuario. */
export function Notificaciones() {
  const qc = useQueryClient();
  const navegar = useNavigate();
  const consulta = useQuery({
    queryKey: ['notificaciones'],
    queryFn: () => api<{ items: Notificacion[]; noLeidas: number }>('/notificaciones'),
    refetchInterval: 120_000,
  });
  const leerTodas = useMutation({
    mutationFn: () => api('/notificaciones/leer-todas', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notificaciones'] }),
  });
  const abrir = async (n: Notificacion) => {
    if (!n.leidaEn) await api(`/notificaciones/${n.id}/leer`, { method: 'POST' }).catch(() => undefined);
    void qc.invalidateQueries({ queryKey: ['notificaciones'] });
    // Solo rutas internas ("/algo?x=y"): nunca se navega a un origen externo.
    if (n.enlace?.startsWith('/') && !n.enlace.startsWith('//')) {
      const [to, qs] = n.enlace.split('?');
      void navegar({ to: to!, search: Object.fromEntries(new URLSearchParams(qs ?? '')) });
    }
  };
  const noLeidas = consulta.data?.noLeidas ?? 0;

  return (
    <Popover>
      <PopoverDisparador asChild>
        <Button variante="fantasma" tamano="icono" className="relative" aria-label={`Notificaciones${noLeidas ? `, ${noLeidas} sin leer` : ''}`}>
          <Bell />
          {noLeidas > 0 && (
            <span className="cifras absolute right-1 top-1 min-w-4 rounded-full bg-accent px-1 text-center text-[10px] font-semibold leading-4 text-on-accent">
              {noLeidas > 9 ? '9+' : noLeidas}
            </span>
          )}
        </Button>
      </PopoverDisparador>
      <PopoverContenido align="end" className="w-[22rem] p-0">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <p className="font-display text-[16px]">Notificaciones</p>
          {noLeidas > 0 && (
            <Button variante="fantasma" tamano="sm" onClick={() => leerTodas.mutate()}>
              <CheckCheck /> Marcar leídas
            </Button>
          )}
        </div>
        <ul className="max-h-[60vh] overflow-y-auto">
          {(consulta.data?.items ?? []).length === 0 && (
            <li className="px-4 py-8 text-center text-[13px] text-foreground-muted">Sin notificaciones</li>
          )}
          {consulta.data?.items.map((n) => (
            <li key={n.id}>
              <button
                type="button"
                onClick={() => void abrir(n)}
                className={cn('flex w-full gap-3 border-b border-border/60 px-4 py-3 text-left hover:bg-muted', !n.leidaEn && 'bg-accent-soft/40')}
              >
                <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', n.leidaEn ? 'bg-transparent' : 'bg-accent')} aria-hidden />
                <span className="min-w-0">
                  <span className="block text-[13.5px] font-medium">{n.titulo}</span>
                  <span className="block text-[12.5px] text-foreground-muted">{n.cuerpo}</span>
                  <span className="cifras mt-0.5 block text-[11.5px] text-foreground-muted">{fechaHora(n.createdAt)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </PopoverContenido>
    </Popover>
  );
}
